import { atom, type Atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithReset, atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';

import { getDocument } from '@atlaskit/browser-apis';

import type { TenantMode } from '../pages/labs/home/ConfigurationPanel/TenantForm';
import { DEFAULT_TENANT_BASE_URL } from '../pages/labs/home/constants';
import type { CypherLspClient } from '../pages/labs/lsp/cypherClient';
import {
	parseParameterValue,
	parameterValueType,
	type ParameterValueType,
} from './parameterValues';

// Base atoms for individual state values
export const TenantUrlAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const TenantModeAtom: PrimitiveAtom<TenantMode> & {
	init: TenantMode;
} = atom<TenantMode>('default');
export const SelectedSiteCloudIdAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const CYPHER_QUERY_STORAGE_KEY = 'graph-central-editor-cypher-query';

/**
 * Only persist the cypher query to localStorage on the Graph Explorer page.
 * Labs users often work across multiple tabs, so sharing query state via
 * localStorage causes them to overwrite each other (see GI-3014).
 */
const isGraphExplorerPage =
	typeof window !== 'undefined' && window.location.pathname.startsWith('/graph-explorer');

export const CypherQueryAtom: WritableAtom<
	string,
	[string | typeof RESET | ((prev: string) => string | typeof RESET)],
	void
> = isGraphExplorerPage
	? atomWithStorage<string>(
			CYPHER_QUERY_STORAGE_KEY,
			'',
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			createJSONStorage<string>(() => localStorage),
			{ getOnInit: true },
		)
	: (atom<string>('') as WritableAtom<
			string,
			[string | typeof RESET | ((prev: string) => string | typeof RESET)],
			void
		>);
export const GraphQLQueryAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');
export const UserStaffIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Parameter rows state
export type ParameterRowType = 'parameter' | 'header';

export type ParameterRowState = {
	id: string;
	type: ParameterRowType | null;
	name: string;
	value: string;
	valueType?: ParameterValueType;
	enabled: boolean;
};

const randomRowId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const createParameterRowState = (overrides?: Partial<ParameterRowState>): ParameterRowState => ({
	id: overrides?.id ?? randomRowId('row'),
	type: overrides?.type ?? null,
	name: overrides?.name ?? '',
	value: overrides?.value ?? '',
	valueType: overrides?.valueType ?? 'STRING',
	enabled: overrides?.enabled ?? true,
});

export const parameterRowIdsBaseAtom: PrimitiveAtom<string[]> & {
	init: string[];
} = atom<string[]>([]);
export const parameterRowsBaseAtom: PrimitiveAtom<Record<string, ParameterRowState>> & {
	init: Record<string, ParameterRowState>;
} = atom<Record<string, ParameterRowState>>({});

const rowsToHeadersMap = (rows: Record<string, ParameterRowState>): Record<string, string> => {
	const result: Record<string, string> = {};
	Object.values(rows).forEach((row) => {
		if (row.type !== 'header' || !row.enabled) {
			return;
		}
		const key = row.name.trim();
		if (key) {
			result[key] = row.value;
		}
	});
	return result;
};

const rowsToParamsMap = (rows: Record<string, ParameterRowState>): Record<string, unknown> => {
	const result: Record<string, unknown> = {};
	Object.values(rows).forEach((row) => {
		if (row.type !== 'parameter' || !row.enabled) {
			return;
		}
		const key = row.name.trim();
		if (key) {
			try {
				result[key] = parseParameterValue(row.value, row.valueType);
			} catch {
				result[key] = row.value;
			}
		}
	});
	return result;
};

const syncRowsForType = (
	get: any,
	set: any,
	type: ParameterRowType,
	entries: Record<string, unknown>,
) => {
	const rows = get(parameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(parameterRowIdsBaseAtom) as string[];

	const nextRows: Record<string, ParameterRowState> = {};
	const nextIds: string[] = [];

	ids.forEach((id) => {
		const row = rows[id];
		if (!row) {
			return;
		}
		if (row.type === type) {
			return;
		}
		nextRows[id] = row;
		nextIds.push(id);
	});

	Object.entries(entries).forEach(([rawName, rawValue]) => {
		const name = rawName.trim();
		if (!name) {
			return;
		}
		const valueType = type === 'parameter' ? parameterValueType(rawValue) : 'STRING';
		const value =
			type === 'parameter' && typeof rawValue !== 'string'
				? (JSON.stringify(rawValue) ?? '')
				: rawValue == null
					? ''
					: String(rawValue);
		let newRow = createParameterRowState({ type, name, value, valueType });
		while (nextRows[newRow.id]) {
			newRow = createParameterRowState({ type, name, value, valueType });
		}
		nextRows[newRow.id] = newRow;
		nextIds.push(newRow.id);
	});

	set(parameterRowsBaseAtom, nextRows);
	set(parameterRowIdsBaseAtom, nextIds);
};

export const parameterRowIdsAtom: Atom<string[]> = atom<string[]>((get) =>
	get(parameterRowIdsBaseAtom),
);
export const parameterRowsAtom: Atom<ParameterRowState[]> = atom<ParameterRowState[]>((get) => {
	const ids = get(parameterRowIdsBaseAtom) as string[];
	const rows = get(parameterRowsBaseAtom) as Record<string, ParameterRowState>;
	return ids.map((id) => rows[id] ?? createParameterRowState({ id }));
});

export type ParameterRowUpdater =
	| Partial<ParameterRowState>
	| ((current: ParameterRowState) => ParameterRowState);

export const updateParameterRowAtom: WritableAtom<
	null,
	[
		payload: {
			id: string;
			updater: ParameterRowUpdater;
		},
	],
	void
> & {
	init: null;
} = atom(null, (get, set, payload: { id: string; updater: ParameterRowUpdater }) => {
	const rows = get(parameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(parameterRowIdsBaseAtom) as string[];
	const { id, updater } = payload;
	const previous = rows[id] ?? createParameterRowState({ id });
	const nextBase =
		typeof updater === 'function'
			? (updater as (current: ParameterRowState) => ParameterRowState)(previous)
			: { ...previous, ...updater };
	const next: ParameterRowState = {
		id: previous.id,
		type: nextBase.type ?? null,
		name: nextBase.name ?? '',
		value: nextBase.value ?? '',
		valueType: nextBase.valueType ?? 'STRING',
		enabled: nextBase.enabled ?? true,
	};
	set(parameterRowsBaseAtom, { ...rows, [id]: next });
	if (!rows[id]) {
		set(parameterRowIdsBaseAtom, [...ids, id]);
	}
});

export const addParameterRowAtom: WritableAtom<
	null,
	[initial?: Partial<ParameterRowState> | undefined],
	void
> & {
	init: null;
} = atom(null, (get, set, initial?: Partial<ParameterRowState>) => {
	const rows = get(parameterRowsBaseAtom) as Record<string, ParameterRowState>;
	const ids = get(parameterRowIdsBaseAtom) as string[];
	let row = createParameterRowState(initial);
	while (rows[row.id]) {
		row = createParameterRowState({ ...initial, id: undefined });
	}
	set(parameterRowsBaseAtom, { ...rows, [row.id]: row });
	set(parameterRowIdsBaseAtom, [...ids, row.id]);
});

export const removeParameterRowAtom: WritableAtom<null, [id: string], void> & {
	init: null;
} = atom(null, (get, set, id: string) => {
	const rows = get(parameterRowsBaseAtom) as Record<string, ParameterRowState>;
	if (!rows[id]) {
		return;
	}
	const ids = get(parameterRowIdsBaseAtom) as string[];
	const { [id]: _removed, ...rest } = rows;
	set(parameterRowsBaseAtom, rest);
	set(
		parameterRowIdsBaseAtom,
		ids.filter((existingId) => existingId !== id),
	);
});

export const clearParameterRowsAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (_get, set) => {
	set(parameterRowsBaseAtom, {});
	set(parameterRowIdsBaseAtom, []);
});

export const HeadersAtom: WritableAtom<Record<string, string>, [update: unknown], void> = atom(
	(get) => rowsToHeadersMap(get(parameterRowsBaseAtom) as Record<string, ParameterRowState>),
	(get, set, update) => {
		const previous = rowsToHeadersMap(
			get(parameterRowsBaseAtom) as Record<string, ParameterRowState>,
		);
		const resolved =
			(typeof update === 'function'
				? (update as (prev: Record<string, string>) => Record<string, string>)(previous)
				: update) ?? {};
		syncRowsForType(get, set, 'header', resolved as Record<string, unknown>);
	},
);

export const ParamsAtom: WritableAtom<Record<string, unknown>, [update: unknown], void> = atom(
	(get) => rowsToParamsMap(get(parameterRowsBaseAtom) as Record<string, ParameterRowState>),
	(get, set, update) => {
		const previous = rowsToParamsMap(
			get(parameterRowsBaseAtom) as Record<string, ParameterRowState>,
		);
		const resolved =
			(typeof update === 'function'
				? (update as (prev: Record<string, unknown>) => Record<string, unknown>)(previous)
				: update) ?? {};
		syncRowsForType(get, set, 'parameter', resolved as Record<string, unknown>);
	},
);

export const IsDebugEnabledAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const FORCE_CONFETTI_STORAGE_KEY = 'twg-labs-force-confetti';

const initialForceConfetti = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(FORCE_CONFETTI_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
})();

export const ForceConfettiAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialForceConfetti);
export const ForceShowFlockAnalyticsAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const GraphQLLimitAtom: PrimitiveAtom<number> & {
	init: number;
} = atom<number>(1000);

export const LABS_DEVELOPER_MODE_STORAGE_KEY = 'twg-labs-developer-mode';
export const LABS_LEFT_SIDEBAR_VISIBLE_STORAGE_KEY = 'twg-labs-left-sidebar-visible';
export const LABS_RESULTS_LAYOUT_STORAGE_KEY = 'twg-labs-results-layout';
export const LABS_RESULTS_VIEW_STORAGE_KEY = 'twg-labs-results-view';
export const LABS_EMBEDDED_EXPLORER_STORAGE_KEY = 'twg-labs-embedded-explorer';
export const LABS_CYPHER_EXPLORER_FEATURE_STORAGE_KEY = 'twg-labs-cypher-explorer-feature';

const initialLabsDeveloperMode = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(LABS_DEVELOPER_MODE_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
})();

export const LabsDeveloperModeAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialLabsDeveloperMode);

const initialLeftSidebarVisible = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return true;
	}
	try {
		const stored = win.localStorage.getItem(LABS_LEFT_SIDEBAR_VISIBLE_STORAGE_KEY);
		return stored === null ? true : stored === 'true';
	} catch {
		return true;
	}
})();

export const LabsLeftSidebarVisibleAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialLeftSidebarVisible);

const initialEmbeddedExplorer = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(LABS_EMBEDDED_EXPLORER_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
})();

export const LabsEmbeddedExplorerAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialEmbeddedExplorer);

const initialCypherExplorerFeature = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(LABS_CYPHER_EXPLORER_FEATURE_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
})();

export const LabsCypherExplorerFeatureAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialCypherExplorerFeature);

const initialResultsVerticalLayout = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return false;
	}
	try {
		return win.localStorage.getItem(LABS_RESULTS_LAYOUT_STORAGE_KEY) === 'vertical';
	} catch {
		return false;
	}
})();

export const LabsResultsVerticalLayoutAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(initialResultsVerticalLayout);

type ResultsViewPreference = 'table' | 'json' | null;

const initialResultsViewPreference: ResultsViewPreference = (() => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!win) {
		return null;
	}
	try {
		const stored = win.localStorage.getItem(LABS_RESULTS_VIEW_STORAGE_KEY);
		return stored === 'table' || stored === 'json' ? stored : null;
	} catch {
		return null;
	}
})();

export const LabsResultsViewPreferenceAtom: PrimitiveAtom<ResultsViewPreference> & {
	init: ResultsViewPreference;
} = atom<ResultsViewPreference>(initialResultsViewPreference);

// Mock errors atom - stores array of error type keys that should be injected
export type MockErrorType = 'something-went-wrong' | 'null-value-non-nullable' | 'validation-error';
export const MockErrorsAtom: PrimitiveAtom<MockErrorType[]> & {
	init: MockErrorType[];
} = atom<MockErrorType[]>([]);

export const AggBaseUrlAtom: Atom<string> = atom(() => DEFAULT_TENANT_BASE_URL);
export const UseExternalGraphQLEndpointAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

// Execution state atoms - using atomWithReset for atoms that clear on execute
export const NetworkResultAtom: WritableAtom<any, [any], void> & {
	init: any;
} = atomWithReset<any>(undefined);
export const RunningAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
export const SpotlightAtom: WritableAtom<
	'cypher' | 'graphql' | null,
	[
		| typeof RESET
		| 'cypher'
		| 'graphql'
		| ((prev: 'cypher' | 'graphql' | null) => typeof RESET | 'cypher' | 'graphql' | null)
		| null,
	],
	void
> & {
	init: 'cypher' | 'graphql' | null;
} = atomWithReset<'cypher' | 'graphql' | null>(null);
export const RoundTripMsAtom: PrimitiveAtom<number | null> & {
	init: number | null;
} = atom<number | null>(null);
export const AuthLoginUrlAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Editor state atoms
export type CypherEditorPosition = {
	line: number;
	column: number;
};

export const CypherEditorPositionAtom: PrimitiveAtom<CypherEditorPosition | null> & {
	init: CypherEditorPosition | null;
} = atom<CypherEditorPosition | null>(null);

export const CypherLspClientAtom: PrimitiveAtom<CypherLspClient | null> & {
	init: CypherLspClient | null;
} = atom<CypherLspClient | null>(null);

export type ReturnLabelEntry = { name: string; labels: string[] };

export const ReturnLabelsAtom: WritableAtom<
	ReturnLabelEntry[] | null,
	[
		| typeof RESET
		| ReturnLabelEntry[]
		| ((prev: ReturnLabelEntry[] | null) => typeof RESET | ReturnLabelEntry[] | null)
		| null,
	],
	void
> & {
	init: ReturnLabelEntry[] | null;
} = atomWithReset<ReturnLabelEntry[] | null>(null);

// Atom to store semantic errors from query execution
export type SemanticError = {
	message: string;
	line?: number;
	column?: number;
};

export const SemanticErrorsAtom: WritableAtom<
	SemanticError[],
	[typeof RESET | SemanticError[] | ((prev: SemanticError[]) => typeof RESET | SemanticError[])],
	void
> & {
	init: SemanticError[];
} = atomWithReset<SemanticError[]>([]);

// Atom to trigger formatting in the Cypher editor
export const TriggerCypherFormatAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

// Atom to store a proposed Cypher query from the AI assistant (for diff view)
export type ProposedCypherQuery = {
	query: string;
};
export const ProposedCypherQueryAtom: PrimitiveAtom<ProposedCypherQuery | null> & {
	init: ProposedCypherQuery | null;
} = atom<ProposedCypherQuery | null>(null);

// UGS schema loading error atom (nodes/relationships schema loading failed)
export const UgsSchemaErrorAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Context API generation state atoms
export type ContextApiData = {
	name: string;
	description: string;
	cypherQuery: string;
	sampledResults: any;
	graphqlQuery: string;
};

export const ContextApiDataAtom: PrimitiveAtom<ContextApiData | null> & {
	init: ContextApiData | null;
} = atom<ContextApiData | null>(null);

export const RovodeSessionLinkAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Exploration override: when non-null, ExecuteQueryAtom uses this instead of
// CypherQueryAtom so the editor's visible Cypher is never mutated during
// relationship discovery / loading.
export const ExplorationCypherOverrideAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Exploration GraphQL override: same idea — prevents the GraphQL editor
// from flickering during exploration queries.
export const ExplorationGraphQLOverrideAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

// Tracks the Cyphie mode used when the current Cypher query was generated by Cyphie.
// null = not from Cyphie, 'default' or 'reasoning' = from Cyphie in that mode.
export type CyphieMode = 'default' | 'reasoning';
export const CyphieModeAtom: PrimitiveAtom<CyphieMode | null> & {
	init: CyphieMode | null;
} = atom<CyphieMode | null>(null);

// Objects view query results - separate from NetworkResultAtom to avoid affecting Labs page
export const ObjectsQueryResultAtom: WritableAtom<any, [any], void> & {
	init: any;
} = atomWithReset<any>(undefined);

// Objects view running state
export const ObjectsQueryRunningAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

// Abort controller for in-flight query requests — allows undo to cancel pending queries.
export const QueryAbortControllerAtom: PrimitiveAtom<AbortController | null> & {
	init: AbortController | null;
} = atom<AbortController | null>(null);
